(() => {
  const bannerMotionDuration = 400;
  const legacySafariMatch = navigator.userAgent.match(/\bVersion\/(\d+)(?:\.\d+)*.*\bSafari\//);
  const needsLegacySafariLazyImageFallback = (
    navigator.vendor === 'Apple Computer, Inc.'
    && legacySafariMatch !== null
    && Number.parseInt(legacySafariMatch[1], 10) <= 16
  );
  let legacySafariLazyMediaObserver;
  let legacySafariInitialViewportsScheduled = false;

  const promoteLegacySafariLazyImage = (image, forceCurrentCandidate = false) => {
    if (!image || image.getAttribute('loading') !== 'lazy') return;

    const selectedSource = image.currentSrc || image.src;
    image.removeAttribute('loading');

    if (!forceCurrentCandidate || !selectedSource || (image.complete && image.naturalWidth > 0)) return;

    // Safari 16 can keep an image in its native lazy queue after `loading` is
    // removed. Pinning the already selected candidate to `src` makes WebKit
    // schedule it as a normal image without changing what the customer sees.
    image.removeAttribute('srcset');
    image.src = selectedSource;
  };

  const promoteLegacySafariInitialViewports = (root = document) => {
    const viewportCutoff = window.innerHeight * 2;
    const images = [...root.querySelectorAll('img[loading="lazy"]')].filter((image) => {
      if (image.closest('[data-hero-slide]:not(.is-active)')) return false;
      if (image.closest('.product-card__image:not(.is-active)')) return false;
      if (image.closest('[data-banner-slide].is-hidden')) return false;

      const bounds = image.getBoundingClientRect();
      const documentTop = bounds.top + window.scrollY;
      return bounds.width > 0 && bounds.height > 0 && documentTop < viewportCutoff;
    });

    images.forEach((image) => promoteLegacySafariLazyImage(image, true));
    const previousCount = Number(document.documentElement.dataset.legacySafariInitialViewportsEager || 0);
    document.documentElement.dataset.legacySafariInitialViewportsEager = String(previousCount + images.length);
  };

  const scheduleLegacySafariInitialViewports = (root = document) => {
    promoteLegacySafariInitialViewports(root);
    if (root !== document || legacySafariInitialViewportsScheduled) return;

    legacySafariInitialViewportsScheduled = true;
    window.requestAnimationFrame(() => promoteLegacySafariInitialViewports(document));
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => promoteLegacySafariInitialViewports(document), { once: true });
    }
  };

  const promoteLegacySafariProductMedia = (media, includeGallery = false) => {
    const images = includeGallery
      ? media.querySelectorAll('.product-card__image img[loading="lazy"]')
      : [
        media.querySelector('.product-card__image.is-active img')
          || media.querySelector('.product-card__image img[loading="lazy"]'),
      ];
    images.forEach(promoteLegacySafariLazyImage);
  };

  const promoteLegacySafariBannerMedia = (banner) => {
    const mobile = window.matchMedia('(max-width: 989px)').matches;
    const slides = mobile
      ? [banner.querySelector('.le-home-module__banner-slide--active')]
      : [...banner.querySelectorAll('[data-banner-slide]:not(.is-hidden)')];
    const imageSelector = mobile
      ? '.le-home-module__banner-image--mobile[loading="lazy"]'
      : '.le-home-module__banner-image--desktop[loading="lazy"]';
    slides.forEach((slide) => promoteLegacySafariLazyImage(slide?.querySelector(imageSelector)));
  };

  const bindLegacySafariLazyMedia = (root = document) => {
    if (!needsLegacySafariLazyImageFallback) return;

    document.documentElement.dataset.legacySafariLazyImageFallback = 'true';
    scheduleLegacySafariInitialViewports(root);
    if (typeof IntersectionObserver !== 'function') return;

    if (!legacySafariLazyMediaObserver) {
      legacySafariLazyMediaObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.target.matches('.product-card__media')) {
            if (!entry.isIntersecting) return;
            promoteLegacySafariProductMedia(entry.target);
            observer.unobserve(entry.target);
            return;
          }

          entry.target.dataset.legacySafariLazyMediaNear = entry.isIntersecting ? 'true' : 'false';
          if (entry.isIntersecting) promoteLegacySafariBannerMedia(entry.target);
        });
      }, { rootMargin: '600px 0px' });
    }

    root.querySelectorAll('.product-card__media').forEach((media) => {
      if (media.dataset.legacySafariLazyMediaBound) return;
      media.dataset.legacySafariLazyMediaBound = 'true';
      legacySafariLazyMediaObserver.observe(media);
      media.addEventListener('pointerenter', () => promoteLegacySafariProductMedia(media, true));
      media.addEventListener('focusin', () => promoteLegacySafariProductMedia(media, true));
    });

    root.querySelectorAll('[data-home-banner]').forEach((banner) => {
      if (banner.dataset.legacySafariLazyMediaBound) return;
      banner.dataset.legacySafariLazyMediaBound = 'true';
      legacySafariLazyMediaObserver.observe(banner);
    });
  };

  const bindHeroCarousel = (hero) => {
    if (hero.dataset.heroCarouselBound) return;
    const track = hero.querySelector('[data-hero-carousel-track]');
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    const previousButton = hero.querySelector('[data-hero-prev]');
    const nextButton = hero.querySelector('[data-hero-next]');
    if (!track || !slides.length) return;

    hero.dataset.heroCarouselBound = 'true';
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncHeaderLogoTone = () => {
      document.documentElement.dataset.homeHeaderLogoTone = slides[activeIndex]?.dataset.headerLogoTone || 'blue';
    };
    const videos = slides.map((slide) => slide.querySelector('[data-hero-video]'));
    videos.forEach((video) => {
      if (!video) return;
      if (needsLegacySafariLazyImageFallback) {
        video.pause();
        video.preload = 'none';
        video.querySelectorAll('source').forEach((source) => source.remove());
        video.load();
        return;
      }
      const markReady = () => video.classList.add('is-ready');
      if (video.readyState >= 2) markReady();
      else video.addEventListener('loadeddata', markReady, { once: true });
    });
    const syncVideoPlayback = () => {
      videos.forEach((video, index) => {
        if (!video) return;
        if (needsLegacySafariLazyImageFallback || index !== activeIndex || reducedMotion.matches || document.hidden) {
          video.pause();
          return;
        }
        video.muted = true;
        video.play().catch(() => {});
      });
    };
    const syncActiveSlide = () => {
      syncHeaderLogoTone();
      syncVideoPlayback();
    };
    syncActiveSlide();
    if (slides.length < 2) {
      reducedMotion.addEventListener('change', syncVideoPlayback);
      document.addEventListener('visibilitychange', syncVideoPlayback);
      return;
    }

    const autoplaySeconds = Math.max(4, Number(hero.dataset.heroAutoplaySeconds) || 6);
    const autoplayEnabled = hero.dataset.heroAutoplay === 'true';
    const mobileSwipe = window.matchMedia('(max-width: 989px)');
    let activePointer = null;
    let suppressClickUntil = 0;
    let autoplayTimer = null;
    let paused = false;

    const bindPointerArrow = (button) => {
      if (!button) return;
      const syncPointerPosition = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const bounds = button.getBoundingClientRect();
        button.style.setProperty('--le-hero-pointer-x', `${event.clientX - bounds.left}px`);
        button.style.setProperty('--le-hero-pointer-y', `${event.clientY - bounds.top}px`);
      };
      button.addEventListener('pointerenter', syncPointerPosition);
      button.addEventListener('pointermove', syncPointerPosition);
      button.addEventListener('pointerleave', () => {
        button.style.removeProperty('--le-hero-pointer-x');
        button.style.removeProperty('--le-hero-pointer-y');
      });
    };

    bindPointerArrow(previousButton);
    bindPointerArrow(nextButton);

    const render = (nextIndex) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(${-activeIndex * 100}%, 0, 0)`;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.tabIndex = active ? 0 : -1;
      });
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      syncActiveSlide();
    };

    const stopAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      stopAutoplay();
      if (!autoplayEnabled || reducedMotion.matches || paused || document.hidden) return;
      autoplayTimer = window.setTimeout(() => {
        render(activeIndex + 1);
        scheduleAutoplay();
      }, autoplaySeconds * 1000);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        render(Number(dot.dataset.heroTarget) || 0);
        scheduleAutoplay();
      });
    });

    const logicalDirection = document.documentElement.dir === 'rtl' ? -1 : 1;
    previousButton?.addEventListener('click', () => {
      render(activeIndex - logicalDirection);
      scheduleAutoplay();
    });
    nextButton?.addEventListener('click', () => {
      render(activeIndex + logicalDirection);
      scheduleAutoplay();
    });

    const resetPointer = () => {
      if (activePointer && track.hasPointerCapture?.(activePointer.id)) {
        track.releasePointerCapture(activePointer.id);
      }
      activePointer = null;
    };

    track.addEventListener('pointerdown', (event) => {
      if (!mobileSwipe.matches || event.pointerType === 'mouse' || !event.isPrimary) return;
      activePointer = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
      };
      track.setPointerCapture?.(event.pointerId);
      stopAutoplay();
    });
    track.addEventListener('pointermove', (event) => {
      if (!activePointer || event.pointerId !== activePointer.id) return;
      activePointer.x = event.clientX;
      activePointer.y = event.clientY;
    });
    track.addEventListener('pointerup', (event) => {
      if (!activePointer || event.pointerId !== activePointer.id) return;
      activePointer.x = event.clientX;
      activePointer.y = event.clientY;
      const distanceX = activePointer.x - activePointer.startX;
      const distanceY = activePointer.y - activePointer.startY;
      resetPointer();
      if (Math.abs(distanceX) >= 36 && Math.abs(distanceX) > Math.abs(distanceY)) {
        suppressClickUntil = window.performance.now() + 400;
        render(activeIndex + (distanceX < 0 ? 1 : -1));
      }
      scheduleAutoplay();
    });
    track.addEventListener('pointercancel', (event) => {
      if (!activePointer || event.pointerId !== activePointer.id) return;
      resetPointer();
      scheduleAutoplay();
    });
    track.addEventListener('click', (event) => {
      if (window.performance.now() > suppressClickUntil) return;
      suppressClickUntil = 0;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    hero.addEventListener('mouseenter', () => {
      paused = true;
      stopAutoplay();
    });
    hero.addEventListener('mouseleave', () => {
      paused = false;
      scheduleAutoplay();
    });
    hero.addEventListener('focusin', () => {
      paused = true;
      stopAutoplay();
    });
    hero.addEventListener('focusout', (event) => {
      if (hero.contains(event.relatedTarget)) return;
      paused = false;
      scheduleAutoplay();
    });
    const handleMotionPreferenceChange = () => {
      syncVideoPlayback();
      scheduleAutoplay();
    };
    const handleVisibilityChange = () => {
      syncVideoPlayback();
      scheduleAutoplay();
    };
    reducedMotion.addEventListener('change', handleMotionPreferenceChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    render(activeIndex);
    scheduleAutoplay();
  };

  const renderBanner = (slides, activeIndex) => {
    const banner = slides[0]?.closest('[data-home-banner]');
    if (!banner) return;
    const previousIndex = (activeIndex - 1 + slides.length) % slides.length;
    const nextIndex = (activeIndex + 1) % slides.length;

    slides.forEach((slide, index) => {
      slide.classList.remove(
        'le-home-module__banner-slide--previous',
        'le-home-module__banner-slide--active',
        'le-home-module__banner-slide--next',
        'is-hidden',
      );
      if (index === activeIndex) slide.classList.add('le-home-module__banner-slide--active');
      else if (index === previousIndex) slide.classList.add('le-home-module__banner-slide--previous');
      else if (index === nextIndex) slide.classList.add('le-home-module__banner-slide--next');
      else slide.classList.add('is-hidden');
      const active = index === activeIndex;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.tabIndex = active ? 0 : -1;
    });

    banner?.querySelectorAll('[data-banner-dots] i').forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
    banner.dataset.homeBannerActiveIndex = String(activeIndex);
    if (banner.dataset.legacySafariLazyMediaNear === 'true') promoteLegacySafariBannerMedia(banner);
  };

  const rotateBanner = (slides, direction) => {
    const banner = slides[0]?.closest('[data-home-banner]');
    if (!banner || banner.dataset.homeBannerAnimating === 'true') return;
    const currentIndex = Number(banner.dataset.homeBannerActiveIndex) || 0;
    activateBanner(slides, currentIndex + direction);
  };

  const activateBanner = (slides, nextIndex) => {
    const banner = slides[0]?.closest('[data-home-banner]');
    if (!banner || banner.dataset.homeBannerAnimating === 'true') return;
    const normalizedIndex = (nextIndex + slides.length) % slides.length;
    const currentIndex = Number(banner.dataset.homeBannerActiveIndex) || 0;
    if (normalizedIndex === currentIndex) return;
    banner.dataset.homeBannerAnimating = 'true';
    renderBanner(slides, normalizedIndex);
    window.setTimeout(() => {
      delete banner.dataset.homeBannerAnimating;
    }, bannerMotionDuration);
  };

  const bindBanner = (banner) => {
    if (banner.dataset.homeBannerBound) return;
    const slides = [...banner.querySelectorAll('[data-banner-slide]')];
    if (slides.length < 2) return;
    banner.dataset.homeBannerBound = 'true';
    const initialIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('le-home-module__banner-slide--active')));
    renderBanner(slides, initialIndex);
    const logicalDirection = document.documentElement.dir === 'rtl' ? -1 : 1;
    banner.querySelector('[data-banner-prev]')?.addEventListener('click', () => rotateBanner(slides, -logicalDirection));
    banner.querySelector('[data-banner-next]')?.addEventListener('click', () => rotateBanner(slides, logicalDirection));
    slides.forEach((slide, index) => {
      slide.addEventListener('click', (event) => {
        const currentIndex = Number(banner.dataset.homeBannerActiveIndex) || 0;
        if (index === currentIndex) return;
        event.preventDefault();
        event.stopPropagation();
        activateBanner(slides, index);
      });
    });

    const track = banner.querySelector('.le-home-module__banner-track');
    let pointerStartX = null;
    track?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      pointerStartX = event.clientX;
    });
    track?.addEventListener('pointerup', (event) => {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) < 36) return;
      rotateBanner(slides, (distance < 0 ? 1 : -1) * logicalDirection);
    });
    track?.addEventListener('pointercancel', () => { pointerStartX = null; });
  };

  const bindCountdown = (node) => {
    if (node.dataset.homeCountdownBound) return;
    node.dataset.homeCountdownBound = 'true';
    const hoursNode = node.querySelector('[data-countdown-hours]');
    const minutesNode = node.querySelector('[data-countdown-minutes]');
    const secondsNode = node.querySelector('[data-countdown-seconds]');
    const update = () => {
      const offset = Number(node.dataset.utcOffsetMinutes || 0) * 60;
      const secondsToday = ((Math.floor(Date.now() / 1000) + offset) % 86400 + 86400) % 86400;
      const remaining = 86400 - secondsToday;
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;
      const hoursText = `${String(hours).padStart(2, '0')} ${node.dataset.hoursLabel || 'h'}`;
      const minutesText = `${String(minutes).padStart(2, '0')} ${node.dataset.minutesLabel || 'm'}`;
      const secondsText = `${String(seconds).padStart(2, '0')} ${node.dataset.secondsLabel || 's'}`;
      if (hoursNode && minutesNode && secondsNode) {
        hoursNode.textContent = hoursText;
        minutesNode.textContent = minutesText;
        secondsNode.textContent = secondsText;
      } else {
        node.textContent = `${hoursText} : ${minutesText} : ${secondsText}`;
      }
      node.setAttribute('aria-label', `${hoursText}, ${minutesText}, ${secondsText}`);
    };
    update();
    window.setInterval(update, 1000);
  };

  const bindShorts = (section) => {
    if (section.dataset.homeShortsBound) return;
    const rail = section.querySelector('[data-home-shorts-rail]');
    if (!rail) return;

    section.dataset.homeShortsBound = 'true';
    const rotate = (direction) => {
      const cards = [...rail.querySelectorAll('[data-home-short-card]')];
      if (cards.length < 2) return;
      if (direction > 0) rail.append(cards[0]);
      else rail.prepend(cards[cards.length - 1]);
    };

    const logicalDirection = document.documentElement.dir === 'rtl' ? -1 : 1;
    section.querySelector('[data-home-shorts-prev]')?.addEventListener('click', () => rotate(-logicalDirection));
    section.querySelector('[data-home-shorts-next]')?.addEventListener('click', () => rotate(logicalDirection));

    const dialog = section.querySelector('[data-home-shorts-dialog]');
    const frame = dialog?.querySelector('[data-home-shorts-frame]');
    const status = dialog?.querySelector('[data-home-shorts-status]');
    const retry = dialog?.querySelector('[data-home-shorts-retry]');
    const destination = dialog?.querySelector('[data-home-shorts-destination]');
    if (!dialog || !frame || !status || !retry || !destination) return;

    let currentTrigger;
    let currentSource = '';
    let timeoutId;
    const loadingLabel = status.dataset.loadingLabel || status.textContent;
    const setState = (state) => {
      window.clearTimeout(timeoutId);
      dialog.dataset.contentState = state;
      status.hidden = state === 'ready';
      retry.hidden = state !== 'error';
      if (state === 'loading') {
        status.textContent = loadingLabel;
        timeoutId = window.setTimeout(() => setState('error'), 12000);
      }
      if (state === 'error') status.textContent = status.dataset.errorLabel || 'Video unavailable';
    };
    const loadCurrentSource = () => {
      if (!currentSource) return;
      setState('loading');
      frame.setAttribute('src', 'about:blank');
      window.requestAnimationFrame(() => frame.setAttribute('src', currentSource));
    };
    const closeDialog = () => {
      if (dialog.open) dialog.close();
    };

    frame.addEventListener('load', () => {
      if (frame.getAttribute('src') !== 'about:blank') setState('ready');
    });
    retry.addEventListener('click', loadCurrentSource);
    dialog.querySelector('[data-home-shorts-close]')?.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', () => {
      window.clearTimeout(timeoutId);
      frame.setAttribute('src', 'about:blank');
      currentTrigger?.focus();
    });

    section.querySelectorAll('[data-home-short-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        currentTrigger = trigger;
        currentSource = trigger.dataset.shortEmbedUrl || '';
        destination.setAttribute('href', trigger.dataset.shortDestinationUrl || '#');
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        loadCurrentSource();
      });
    });
  };

  const bindRecommendationsMosaic = (section) => {
    if (section.dataset.recommendationsMosaicBound) return;
    const groups = [...section.querySelectorAll('.le-recommendations-group--mosaic')];
    if (!groups.length) return;

    section.dataset.recommendationsMosaicBound = 'true';
    let frameId;
    const sync = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        groups.forEach((group) => {
          if (!desktop) {
            group.style.removeProperty('--le-rec-feature-media-runtime');
            return;
          }

          const featureMedia = group.querySelector('.product-card--feature-desktop .product-card__media');
          const compactMedia = [...group.querySelectorAll(
            '.product-card:not(.product-card--feature-desktop) .product-card__media',
          )];
          if (!featureMedia || compactMedia.length < 4) return;

          const firstTop = Math.min(...compactMedia.map((media) => media.getBoundingClientRect().top));
          const secondRow = compactMedia.filter((media) => media.getBoundingClientRect().top > firstTop + 1);
          if (!secondRow.length) return;

          const featureTop = featureMedia.getBoundingClientRect().top;
          const secondRowBottom = Math.max(...secondRow.map((media) => media.getBoundingClientRect().bottom));
          const nextHeight = Math.max(0, secondRowBottom - featureTop);
          const currentHeight = Number.parseFloat(
            group.style.getPropertyValue('--le-rec-feature-media-runtime'),
          ) || 0;
          if (Math.abs(currentHeight - nextHeight) > 0.5) {
            group.style.setProperty('--le-rec-feature-media-runtime', `${nextHeight}px`);
          }
        });
      });
    };

    sync();
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(sync);
      observer.observe(section);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }
  };

  const init = (root = document) => {
    bindLegacySafariLazyMedia(root);
    root.querySelectorAll('[data-hero-carousel]').forEach(bindHeroCarousel);
    root.querySelectorAll('[data-home-banner]').forEach(bindBanner);
    root.querySelectorAll('[data-home-countdown]').forEach(bindCountdown);
    root.querySelectorAll('[data-home-shorts]').forEach(bindShorts);
    root.querySelectorAll('[data-home-section="recommendations"]').forEach(bindRecommendationsMosaic);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
